/**
 * Custom Indicator Scripting Engine
 *
 * Allows users to write custom indicators using a simple formula language.
 * The formula is evaluated per-bar and produces one or more plot values.
 *
 * Syntax:
 *   PLOT <name> = <expression>
 *   // Optional: PANE separate  (render in separate pane instead of overlay)
 *   // Optional: COLOR <name> = #hex
 *
 * Expressions:
 *   CLOSE, OPEN, HIGH, LOW, VOLUME
 *   SMA(CLOSE, 14), EMA(CLOSE, 21)
 *   RSI(14), ATR(14)
 *   PREV(expression, barsBack)
 *   Arithmetic: +, -, *, /
 *   Ternary: IF condition THEN expr ELSE expr
 *
 * Example:
 *   // Custom spread indicator
 *   PLOT spread = EMA(CLOSE, 9) - EMA(CLOSE, 21)
 *   PLOT zero = 0
 *
 *   // Custom momentum
 *   PANE separate
 *   PLOT momentum = CLOSE - PREV(CLOSE, 10)
 *   COLOR momentum = #ff9800
 */

import { IndicatorDefinition, PlotConfig, PlotData } from "../indicators/types/indicator.types";
import { CandleData } from "../types/chart.types";

interface CustomPlot {
  name: string;
  expression: string;
  color?: string;
}

interface CustomIndicatorDef {
  name: string;
  plots: CustomPlot[];
  pane: "overlay" | "separate";
}

const STORAGE_KEY = "marketview_custom_indicators";

export function loadCustomIndicators(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export function saveCustomIndicator(name: string, code: string): void {
  const all = loadCustomIndicators();
  all[name] = code;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

export function deleteCustomIndicator(name: string): void {
  const all = loadCustomIndicators();
  delete all[name];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
}

/**
 * Parse custom indicator code into a definition
 */
export function parseCustomIndicator(name: string, code: string): CustomIndicatorDef {
  const lines = code.split("\n");
  const plots: CustomPlot[] = [];
  let pane: "overlay" | "separate" = "overlay";

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("//")) continue;

    const upper = trimmed.toUpperCase();

    if (upper === "PANE SEPARATE") {
      pane = "separate";
      continue;
    }

    if (upper.startsWith("PLOT ")) {
      const rest = trimmed.slice(5).trim();
      const eqIdx = rest.indexOf("=");
      if (eqIdx > 0) {
        const plotName = rest.slice(0, eqIdx).trim();
        const expression = rest.slice(eqIdx + 1).trim();
        plots.push({ name: plotName, expression });
      }
    }

    if (upper.startsWith("COLOR ")) {
      const rest = trimmed.slice(6).trim();
      const eqIdx = rest.indexOf("=");
      if (eqIdx > 0) {
        const plotName = rest.slice(0, eqIdx).trim();
        const color = rest.slice(eqIdx + 1).trim();
        const plot = plots.find(p => p.name.toLowerCase() === plotName.toLowerCase());
        if (plot) plot.color = color;
      }
    }
  }

  return { name, plots, pane };
}

const DEFAULT_COLORS = ["#2196f3", "#ff9800", "#4caf50", "#f44336", "#9c27b0", "#00bcd4"];

/**
 * Convert a parsed custom indicator into an IndicatorDefinition
 * compatible with the existing indicator system.
 */
export function customIndicatorToDefinition(parsed: CustomIndicatorDef): IndicatorDefinition {
  const indicatorPlots: PlotConfig[] = parsed.plots.map((p, i) => ({
    key: p.name.toLowerCase().replace(/\s+/g, "_"),
    label: p.name,
    color: p.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
    style: "line" as const,
    lineWidth: 2,
  }));

  return {
    id: `custom_${parsed.name.toLowerCase().replace(/\s+/g, "_")}`,
    name: parsed.name,
    shortName: parsed.name.slice(0, 6).toUpperCase(),
    category: "custom" as any,
    pane: parsed.pane,
    params: [],
    plots: indicatorPlots,
    calculate: (candles: CandleData[]) => {
      return evaluateCustomIndicatorPlotData(parsed, candles);
    },
  };
}

/**
 * Evaluate all plots of a custom indicator and return PlotData[] format.
 */
function evaluateCustomIndicatorPlotData(
  def: CustomIndicatorDef,
  candles: CandleData[],
): PlotData[] {
  const cache = new EvalCache(candles);
  const results: PlotData[] = [];

  for (const plot of def.plots) {
    const key = plot.name.toLowerCase().replace(/\s+/g, "_");
    const data: { time: number; value: number }[] = [];

    for (let i = 0; i < candles.length; i++) {
      try {
        const val = evaluateExpr(plot.expression, candles, i, cache);
        if (val !== null && !isNaN(val)) {
          data.push({ time: candles[i].time, value: val });
        }
      } catch {
        // skip bar on error
      }
    }

    results.push({ key, data });
  }

  return results;
}

/**
 * Evaluate all plots of a custom indicator against candle data.
 * Returns record format (used internally).
 */
function evaluateCustomIndicator(
  def: CustomIndicatorDef,
  candles: CandleData[],
): Record<string, (number | null)[]> {
  const result: Record<string, (number | null)[]> = {};
  const cache = new EvalCache(candles);

  for (const plot of def.plots) {
    const key = plot.name.toLowerCase().replace(/\s+/g, "_");
    const values: (number | null)[] = [];

    for (let i = 0; i < candles.length; i++) {
      try {
        values.push(evaluateExpr(plot.expression, candles, i, cache));
      } catch {
        values.push(null);
      }
    }

    result[key] = values;
  }

  return result;
}

// ─── Expression Evaluator ───────────────────────────────────────

class EvalCache {
  private cache: Map<string, number[]> = new Map();
  readonly candles: CandleData[];

  constructor(candles: CandleData[]) {
    this.candles = candles;
  }

  getSMA(period: number): number[] {
    return this.getOrCompute(`sma_${period}`, () => {
      const closes = this.candles.map(c => c.close);
      const result: number[] = [];
      for (let i = 0; i < closes.length; i++) {
        if (i < period - 1) { result.push(NaN); continue; }
        let sum = 0;
        for (let j = i - period + 1; j <= i; j++) sum += closes[j];
        result.push(sum / period);
      }
      return result;
    });
  }

  getEMA(period: number): number[] {
    return this.getOrCompute(`ema_${period}`, () => {
      const closes = this.candles.map(c => c.close);
      const k = 2 / (period + 1);
      const result: number[] = [];
      let ema = NaN;
      for (let i = 0; i < closes.length; i++) {
        if (i < period - 1) { result.push(NaN); continue; }
        if (i === period - 1) {
          let s = 0;
          for (let j = 0; j < period; j++) s += closes[j];
          ema = s / period;
        } else {
          ema = closes[i] * k + ema * (1 - k);
        }
        result.push(ema);
      }
      return result;
    });
  }

  getRSI(period: number): number[] {
    return this.getOrCompute(`rsi_${period}`, () => {
      const data = this.candles.map(c => c.close);
      const result: number[] = new Array(data.length).fill(50);
      if (data.length < period + 1) return result;

      let avgGain = 0, avgLoss = 0;
      for (let i = 1; i <= period; i++) {
        const ch = data[i] - data[i - 1];
        if (ch > 0) avgGain += ch; else avgLoss += Math.abs(ch);
      }
      avgGain /= period; avgLoss /= period;
      result[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

      for (let i = period + 1; i < data.length; i++) {
        const ch = data[i] - data[i - 1];
        avgGain = (avgGain * (period - 1) + (ch > 0 ? ch : 0)) / period;
        avgLoss = (avgLoss * (period - 1) + (ch < 0 ? Math.abs(ch) : 0)) / period;
        result[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
      }
      return result;
    });
  }

  getATR(period: number): number[] {
    return this.getOrCompute(`atr_${period}`, () => {
      const c = this.candles;
      const result: number[] = new Array(c.length).fill(0);
      const tr: number[] = [];
      for (let i = 0; i < c.length; i++) {
        if (i === 0) tr.push(c[i].high - c[i].low);
        else tr.push(Math.max(c[i].high - c[i].low, Math.abs(c[i].high - c[i - 1].close), Math.abs(c[i].low - c[i - 1].close)));
      }
      if (tr.length >= period) {
        let s = 0;
        for (let i = 0; i < period; i++) s += tr[i];
        result[period - 1] = s / period;
        for (let i = period; i < tr.length; i++) result[i] = (result[i - 1] * (period - 1) + tr[i]) / period;
      }
      return result;
    });
  }

  private getOrCompute(key: string, fn: () => number[]): number[] {
    if (!this.cache.has(key)) this.cache.set(key, fn());
    return this.cache.get(key)!;
  }
}

function evaluateExpr(expr: string, candles: CandleData[], barIndex: number, cache: EvalCache): number | null {
  expr = expr.trim();
  const upper = expr.toUpperCase();

  // 1) Number literal
  const num = parseFloat(expr);
  if (!isNaN(num) && /^-?\d+\.?\d*$/.test(expr)) return num;

  // 2) Fields
  const bar = candles[barIndex];
  if (!bar) return null;
  if (upper === "CLOSE") return bar.close;
  if (upper === "OPEN") return bar.open;
  if (upper === "HIGH") return bar.high;
  if (upper === "LOW") return bar.low;
  if (upper === "VOLUME") return bar.volume ?? 0;

  // 3) Top-level arithmetic: find lowest-precedence + or - outside parens
  let depth = 0;
  for (let i = expr.length - 1; i >= 0; i--) {
    if (expr[i] === ")") depth++;
    if (expr[i] === "(") depth--;
    if (depth === 0 && (expr[i] === "+" || expr[i] === "-") && i > 0) {
      const left = evaluateExpr(expr.slice(0, i), candles, barIndex, cache);
      const right = evaluateExpr(expr.slice(i + 1), candles, barIndex, cache);
      if (left === null || right === null) return null;
      return expr[i] === "+" ? left + right : left - right;
    }
  }
  // * and /
  depth = 0;
  for (let i = expr.length - 1; i >= 0; i--) {
    if (expr[i] === ")") depth++;
    if (expr[i] === "(") depth--;
    if (depth === 0 && (expr[i] === "*" || expr[i] === "/") && i > 0) {
      const left = evaluateExpr(expr.slice(0, i), candles, barIndex, cache);
      const right = evaluateExpr(expr.slice(i + 1), candles, barIndex, cache);
      if (left === null || right === null) return null;
      return expr[i] === "*" ? left * right : (right !== 0 ? left / right : null);
    }
  }

  // 4) Parenthesized
  if (expr.startsWith("(") && expr.endsWith(")")) {
    return evaluateExpr(expr.slice(1, -1), candles, barIndex, cache);
  }

  // 5) PREV(expr, N)
  if (upper.startsWith("PREV(") && expr.endsWith(")")) {
    const inner = expr.slice(5, -1);
    const lastComma = inner.lastIndexOf(",");
    if (lastComma > 0) {
      const exprPart = inner.slice(0, lastComma).trim();
      const n = parseInt(inner.slice(lastComma + 1).trim());
      if (barIndex - n < 0) return null;
      return evaluateExpr(exprPart, candles, barIndex - n, cache);
    }
  }

  // 6) SMA(source, period) or SMA(period) (defaults to CLOSE)
  if (upper.startsWith("SMA(") && expr.endsWith(")")) {
    const args = expr.slice(4, -1).split(",").map(a => a.trim());
    if (args.length === 1) {
      const period = parseInt(args[0]);
      const values = cache.getSMA(period);
      return isNaN(values[barIndex]) ? null : values[barIndex];
    }
    if (args.length === 2) {
      const period = parseInt(args[1]);
      const values = cache.getSMA(period);
      return isNaN(values[barIndex]) ? null : values[barIndex];
    }
  }

  // 7) EMA(source, period) or EMA(period)
  if (upper.startsWith("EMA(") && expr.endsWith(")")) {
    const args = expr.slice(4, -1).split(",").map(a => a.trim());
    const period = parseInt(args[args.length === 2 ? 1 : 0]);
    const values = cache.getEMA(period);
    return isNaN(values[barIndex]) ? null : values[barIndex];
  }

  // 8) RSI(period)
  if (upper.startsWith("RSI(") && expr.endsWith(")")) {
    const period = parseInt(expr.slice(4, -1).trim());
    const values = cache.getRSI(period);
    return values[barIndex];
  }

  // 9) ATR(period)
  if (upper.startsWith("ATR(") && expr.endsWith(")")) {
    const period = parseInt(expr.slice(4, -1).trim());
    const values = cache.getATR(period);
    return values[barIndex] || null;
  }

  // 10) ABS(expr)
  if (upper.startsWith("ABS(") && expr.endsWith(")")) {
    const inner = evaluateExpr(expr.slice(4, -1), candles, barIndex, cache);
    return inner !== null ? Math.abs(inner) : null;
  }

  // 11) MAX(a, b) / MIN(a, b)
  if (upper.startsWith("MAX(") && expr.endsWith(")")) {
    const inner = expr.slice(4, -1);
    const comma = findTopLevelComma(inner);
    if (comma > 0) {
      const a = evaluateExpr(inner.slice(0, comma), candles, barIndex, cache);
      const b = evaluateExpr(inner.slice(comma + 1), candles, barIndex, cache);
      if (a === null || b === null) return null;
      return Math.max(a, b);
    }
  }
  if (upper.startsWith("MIN(") && expr.endsWith(")")) {
    const inner = expr.slice(4, -1);
    const comma = findTopLevelComma(inner);
    if (comma > 0) {
      const a = evaluateExpr(inner.slice(0, comma), candles, barIndex, cache);
      const b = evaluateExpr(inner.slice(comma + 1), candles, barIndex, cache);
      if (a === null || b === null) return null;
      return Math.min(a, b);
    }
  }

  return null;
}

function findTopLevelComma(str: string): number {
  let depth = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === "(") depth++;
    if (str[i] === ")") depth--;
    if (depth === 0 && str[i] === ",") return i;
  }
  return -1;
}
